import _isPlainObject from 'lodash/isPlainObject';
import _startCase from 'lodash/startCase';
import CaptureLogsMutation from '@src/graphql/mutations/CaptureLogs.gql';
import CurrentCompanyQuery from '@/graphql/queries/CurrentCompany.gql';
import CurrentUserQuery from '@/graphql/queries/CurrentUser.gql';
import pluralize from 'pluralize';
import * as Sentry from '@sentry/browser';
import { getLocalePreferences, setI18nFromAcceptLanguage, setI18nFromLocale, mergeLocaleMessages } from '@src/i18n';
import { getDefaults } from '@src/i18n/defaults';
import { getDemoOverrides } from '@src/lib/demo';
import { redirectToNewPlatformIfEligible } from '@src/lib/platformMigrationRedirect';

export default {
  apollo: {
    company: {
      query: CurrentCompanyQuery,
      update({ company }) {
        if (process.env.VUE_ENV === 'client' && company && company.handle === 'real-links-demo') {
          // @NOTE Can't rely on isDemoSite as it hasn't been set here yet!
          const { i18n, branding, preferences } = getDemoOverrides();
          company.i18n = { ...company.i18n, ...i18n };
          company.siteBranding = { ...company.siteBranding, ...branding };
          company.sitePreferences = { ...company.sitePreferences, ...preferences };
        }
        return company;
      },
      result({ loading, data }) {
        if (!loading && data && data.company && data.company.id) {
          const { company: { handle, i18n: i18nStrings, templatedStrings: legacyTemplates } } = data;
          mergeLocaleMessages.call(this, this.$i18n, data.company, legacyTemplates, {
            ...getDefaults.call(this, data.company),
            ...i18nStrings
          });

          if (process.env.VUE_ENV === 'server') {
            setI18nFromAcceptLanguage.call(this, this.$i18n, this.$serverReq.acceptLanguages);
          } else if (process.env.VUE_ENV === 'client') {
            Sentry.setTags({
              company: handle,
            });

            if (!this.user || !this.user.locale) {
              const acceptLanguages = Array.isArray(window.navigator.languages) && window.navigator.languages.length
                ? window.navigator.languages
                : [ window.navigator.language ];
              setI18nFromAcceptLanguage.call(this, this.$i18n, acceptLanguages);
            }

            Sentry.setTags({ locale: this.$i18n.locale });

            redirectToNewPlatformIfEligible(this);
          }
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: process.env.VUE_ENV === 'server' ? 'cache-first' : 'cache-only',
      prefetch: process.env.VUE_ENV === 'server',
      // skip() {
      //   if (process.env.ADMIN_UI) {
      //     const { companyID } = this.$route.params;
      //     return !companyID;
      //   } else {
      //     return false;
      //   }
      // },
    },
    user: {
      query: CurrentUserQuery,
      result({ loading, data }) {
        if (!loading && process.env.VUE_ENV === 'client' && data && data.user && data.user.id) {
          const { id, emailAddress, role, locale } = data.user;

          Sentry.setTags({ userRole: role });
          Sentry.setUser({
            id,
            email: (process.env.ADMIN_UI || `${emailAddress}`.includes('@reallinks.')) ? emailAddress : null,
            role,
          });

          if (locale) {
            setI18nFromLocale.call(this, this.$i18n, locale);
          }

          Sentry.setTags({ locale: this.$i18n.locale });
        }
      },
      fetchPolicy: 'cache-only',
      prefetch: false,
    },
  },
  metaInfo() {
    const { locale: lang } = this.$i18n;
    return {
      htmlAttrs: {
        lang,
      },
      meta: [
        { charset: 'utf-8' },
        { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge,chrome=1' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0, user-scalable=no, viewport-fit=cover' },
        // { name: 'robots', content: 'noindex, nofollow, nocache, noimageindex', vmid: 'robots' },
        { name: 'theme-color', content: '#ffffff', vmid: 'theme-color' },
      ],
    };
  },
  computed: {
    authServiceOrigin() {
      return this.isProd ? 'https://auth.reallinks.io' : 'https://auth.reallinks.dev';
    },
    companyID() {
      return this.company && typeof this.company.id === 'string' ? this.company.id : null;
    },
    companyHandle() {
      return this.company && typeof this.company.handle === 'string' ? this.company.handle : null;
    },
    companyName() {
      const { name, branding, siteBranding } = this.company || {};
      const { name: brandName } = { ...branding, ...siteBranding };
      return brandName || name || null;
    },
    userID() {
      return this.user && typeof this.user.id === 'string' ? this.user.id : null;
    },
    userSegmentIDs() {
      return (this.user && Array.isArray(this.user.segments) ? this.user.segments : []).map(({ id }) => id);
    },
    adminOrigin() {
      return this.isProd ? 'https://admin.reallinks.io' : 'https://admin.reallinks.dev';
    },
    canSyncContacts() {
      const { canSyncContacts } = this.company ? this.company.sitePreferences : {};
      return canSyncContacts === true;
    },
    currentUserID() {
      return this.user && this.user.id ? this.user.id : null;
    },
    departmentLabel() {
      const { departmentLabel } = this.company ? this.company.sitePreferences : {};
      return _startCase(departmentLabel || 'Department');
    },
    departmentLabelPlural() {
      const { departmentLabel } = this.company ? this.company.sitePreferences : {};
      return pluralize(_startCase(departmentLabel || 'Department'));
    },
    isAdmin() {
      return process.env.ADMIN_UI === true ||
        [ 'https://admin.reallinks.io', 'https://admin.reallinks.dev' ]
          .includes(process.env.PROXY_ORIGIN || process.env.PROXY_API_URL || this.origin);
    },
    isCampaignsEnabled() {
      const { preferences, sitePreferences } = this.company || {};
      const { isCampaignsEnabled } = { ...preferences, ...sitePreferences };
      return isCampaignsEnabled === true;
    },
    isSimplifiedCampaign() {
      const { isSimplifiedCampaign } = this.company ? this.company.sitePreferences : {};
      return isSimplifiedCampaign === true;
    },
    isEnumLocation() {
      const { isEnumLocation } = this.company ? this.company.sitePreferences : {};
      return isEnumLocation === true;
    },
    isProd() {
      const origin = `${process.env.PROXY_ORIGIN || process.env.PROXY_API_URL || this.origin}`;
      return (origin.endsWith('reallinks.dev') || origin.startsWith('http://localhost')) === false;
    },
    isLoggedIn() {
      return Boolean(this.user && this.user.id);
    },
    isRecruiter() {
      return this.isLoggedIn && [ 'SUPER_ADMIN', 'RECRUITER_ADMIN', 'RECRUITER' ].includes(this.user.role);
    },
    isRecruiterAdmin() {
      return this.isLoggedIn && [ 'SUPER_ADMIN', 'RECRUITER_ADMIN' ].includes(this.user.role);
    },
    isSuperAdmin() {
      const { id, role } = this.isLoggedIn ? this.user : {};
      return id === 'ADMIN' || role === 'SUPER_ADMIN';
    },
    isAppReviewer() {
      const { emailAddress } = this.user || {};
      return emailAddress === 'apps@reallinks.dev';
    },
    isReferralsFeatureEnabled() {
      const { isReferralsFeatureEnabled } = this.company ? this.company.sitePreferences : {};
      return isReferralsFeatureEnabled === true;
    },
    isInternalMobilityEnabled() {
      const { isInternalMobilityEnabled } = this.company ? this.company.sitePreferences : {};
      return isInternalMobilityEnabled === true;
    },
    isMentorshipFeatureEnabled() {
      const { isMentorshipFeatureEnabled } = this.company ? this.company.sitePreferences : {};
      return isMentorshipFeatureEnabled === true;
    },
    isReferralJamsEnabled() {
      const { isReferralJamsEnabled } = this.company ? this.company.sitePreferences : {};
      return this.isStandaloneReferralJams || isReferralJamsEnabled === true;
    },
    isStandaloneReferralJams() {
      const origin = `${process.env.PROXY_ORIGIN || process.env.PROXY_API_URL || this.origin}`;
      return origin === 'https://referraljam.io' || `${origin}`.endsWith('.referraljam.io');
    },
    isOnlyMentorship() {
      const { isMentorshipFeatureEnabled, isReferralsFeatureEnabled, isInternalMobilityEnabled } = this.company ? this.company.sitePreferences : {};
      return isMentorshipFeatureEnabled === true && isReferralsFeatureEnabled === false && isInternalMobilityEnabled === false;
    },
    isCommunityApplicant() {
      if (this.isCommunitySite && !this.isRecruiter) {
        const { customFields } = this.user || {};
        return Array.isArray(customFields) && customFields.length
          ? (customFields.find(({ key }) => key === 'REFERER-BOOL') || {}).value === 'I\'m looking for a new role'
          : false;
      } else {
        return false;
      }
    },
    isInterestBasedMatching() {
      const { isInterestBasedMatchingEnabled } = this.company ? this.company.sitePreferences : {};
      return isInterestBasedMatchingEnabled === true;
    },
    allowInvitations() {
      const { allowInvitations } = this.company ? this.company.sitePreferences : {};
      return allowInvitations === true;
    },
    canMessageNativeApp() {
      return window && window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function';
    },
    servicesOrigin() {
      return this.isProd ? 'https://services.reallinks.io' : 'https://services.reallinks.dev';
    },
    isRewardsEnabled() {
      const { isRewardsEnabled } = this.company ? this.company.sitePreferences : {};
      return isRewardsEnabled === true;
    },
    isNewDashboard() {
      const { company } = this;
      return company && company.sitePreferences && company.sitePreferences.isNewDashboard;
    },
    supportedLanguages() {
      // const { supportedLocales } = this.company || {};
      const { supportedLanguages } = getLocalePreferences(this.company);
      return Array.isArray(supportedLanguages) ? supportedLanguages : [];
    },
    isEnglishLang() {
      return [ 'en' ].includes(this.$i18n.locale);
    },

    ...(customClients => Object.keys(customClients).reduce((cs, key) => {
      cs[key] = function () {
        return customClients[key].includes(process.env.COMPANY_HANDLE || this.companyHandle);
      };
      return cs;
    }, {}))({
      isApplusIdiada: [ 'applusidiada' ],
      isAssurant: [ 'assurant' ],
      isBallastNedam: [ 'ballast-nedam', 'ballastnedam' ],
      isBRC: [ 'redcross' ],
      isBT: [ 'bt' ],
      isBerenschot: [ 'berenschot' ],
      isBupaDental: [ 'bupadental' ],
      isCapco: [ 'capco' ],
      isCelonis: [ 'celonis', 'celogrow' ],
      isChalhoub: [ 'chalhoub' ],
      isCommunitySite: [ 'community' ],
      isCXR: [ 'cxr' ],
      isDemoSite: [ 'real-links-demo' ],
      isERM: [ 'erm' ],
      isEventsSite: [ 'real-links-events' ],
      isEXPD8: [ 'expd8rewards', 'expd8' ],
      isEY: [ 'ey' ],
      isGROWMARK: [ 'growmark' ],
      isGWR: [ 'gwr' ],
      isGWRReferrals: [ 'gwr-referrals' ],
      isHelloFresh: [ 'hellofresh' ],
      isHSBCDemo: [ 'hsbc-demo' ],
      isIdemia: [ 'idemia' ],
      isInforma: [ 'informa' ],
      isInMobi: [ 'inmobi' ],
      isKP: [ 'kpfilms' ],
      isLNW: [ 'lnw' ],
      isMyDentist: [ 'mydentist' ],
      isNielsenIQ: [ 'nielseniq' ],
      isOcadoGroup: [ 'ocadogroup' ],
      isOLX: [ 'olx' ],
      isOptiver: [ 'optiver' ],
      isOptiverInterns: [ 'optiver-interns' ],
      isPayPal: [ 'paypal', 'paypal-demo' ],
      isPicnic: [ 'picnic' ],
      isProductMadness: [ 'productmadness' ],
      isSainsburys: [ 'sainsburys', 'sainsburys-demo' ],
      isStoryblok: [ 'storyblok' ],
      isSWR: [ 'swr' ],
      isTaylorAndFrancis: [ 'taylorandfrancis' ],
      isThales: [ 'thales' ],
      isViooh: [ 'viooh' ],
      isVoltaTrucks: [ 'voltatrucks' ],
      isWickes: [ 'wickes' ],
    }),
  },
  methods: {
    onError(err) {
      this.$log.error(err);
      Sentry.captureException(err);
    },
    captureLog(log, ...args) {
      return this.captureLogs([ log ], ...args);
    },
    async captureLogs(logs, onUpdate) {
      const { valid, invalid } = logs.reduce((result, log) => {
        try {
          log = formatLog.call(this, log);
          result.valid.push(log);
        } catch (err) {
          result.invalid.push([ log, err ]);
        }
        return result;
      }, {
        valid: [],
        invalid: [],
      });

      if (invalid.length) {
        invalid.forEach(([ log, err ]) => {
          Sentry.withScope(scope => {
            scope.setTag('function', '$captureLogs');
            scope.setExtra(log, JSON.stringify(log));
            Sentry.captureException(err);
          });

          if (process.env.NODE_ENV === 'development') {
            this.$log.error('Error validating log', err);
            this.$notifyErr(err, {
              title: 'Error validating log',
              append: JSON.stringify(log, null, 2),
            });
          }
        });
      }

      if (valid.length) {
        try {
          await this.$apollo.mutate({
            mutation: CaptureLogsMutation,
            variables: { logs: valid },
            update: typeof onUpdate === 'function' ? onUpdate : null,
          });

          valid.forEach(log => this.$emit('capturedLog', log));
        } catch (err) {
          Sentry.withScope(scope => {
            scope.setTag('function', '$captureLogs');
            Sentry.captureException(err);
          });

          if (process.env.NODE_ENV === 'development') {
            this.$log.error('Error with CaptureLogsMutation', err, valid);
            this.$notifyErr(err, {
              title: 'Error with CaptureLogsMutation',
            });
          }
        }
      }
    },
    messageNativeApp(data) {
      window.ReactNativeWebView.postMessage(JSON.stringify(data));
    },
  },
};

export function mapRoot(keys) {
  /**
   * Normalize the map
   * normalizeMap([1, 2, 3]) => [ { key: 1, val: 1 }, { key: 2, val: 2 }, { key: 3, val: 3 } ]
   * normalizeMap({a: 1, b: 2, c: 3}) => [ { key: 'a', val: 1 }, { key: 'b', val: 2 }, { key: 'c', val: 3 } ]
   * @link https://github.com/vuejs/vuex/blob/ffc7d00314a371c09d288bc5123793031d182cd2/src/helpers.js#L124-L135
   */
  const normalized = Array.isArray(keys)
    ? keys.map(key => ({ key, value: key }))
    : Object.keys(keys).map(key => ({ key, value: keys[key] }));
  return normalized.reduce((res, { key, value }) => {
    res[key] = function () {
      return typeof value === 'function' ? value.call(this, this.$root) : this.$root[value];
    };
    return res;
  }, {});
}

export function mapPreferences(keys) {
  /**
   * Normalize the map
   * normalizeMap([1, 2, 3]) => [ { key: 1, val: 1 }, { key: 2, val: 2 }, { key: 3, val: 3 } ]
   * normalizeMap({a: 1, b: 2, c: 3}) => [ { key: 'a', val: 1 }, { key: 'b', val: 2 }, { key: 'c', val: 3 } ]
   * @link https://github.com/vuejs/vuex/blob/ffc7d00314a371c09d288bc5123793031d182cd2/src/helpers.js#L124-L135
   */
  const normalized = Array.isArray(keys)
    ? keys.map(key => ({ key, value: key }))
    : Object.keys(keys).map(key => ({ key, value: keys[key] }));
  return normalized.reduce((res, { key, value }) => ({
    ...res,
    [key]: function () { // eslint-disable-line object-shorthand
      const { preferences, sitePreferences } = this.$root.company || {};
      const prefs = { ...sitePreferences, ...preferences };
      return typeof value === 'function'
        ? value.call(this, prefs)
        : Object.prototype.hasOwnProperty.call(prefs, value) ? prefs[value] : null;
    },
  }), {});
}

export function formatLog(log) {
  const { eventName } = log;
  if (!eventName || typeof eventName !== 'string' || !eventName.length) {
    throw new TypeError('Missing eventName from log');
  }

  if (log.user && log.user.self === true) {
    if (this.user && this.user.id) {
      log.user = { id: this.user.id };
    } else {
      throw new Error('User is unauthenticated, { user: { self: true } } fails');
    }
  }

  if (sessionStorage) {
    const campID = sessionStorage.getItem('campID');
    const compID = sessionStorage.getItem('compID');
    if (!log.campaign && campID) {
      log.campaign = { id: campID };
    }
    if (!log.campaignComponent && compID) {
      log.campaignComponent = { id: compID };
    }
  }

  if (_isPlainObject(log.data)) {
    log.dataHash = { ...log.data };
    delete log.data;
  } else if (Array.isArray(log.data)) {
    log.dataHash = log.data.reduce((r, { key, value }) => ({ ...r, [key]: value }), {});
    delete log.data;
  }

  return log;
}



// WEBPACK FOOTER //
// ./src/mixins/root.js